import { useEffect, useRef } from 'react';

const ACCOUNT = 'Mm5pW7Mp';
type EmbedOptions = Record<string, unknown>;
type Player = { destroy?: () => void; dispose?: () => void; pause?: () => void };
type EmbedConstructor = { new (): { run: (id: string) => unknown }; isMock?: boolean };
type VidalyticsWindow = Window & {
  Vidalytics?: { Embed?: EmbedConstructor; embedQueue?: { embedId: string }[] };
  VidalyticsL?: { Loader?: new () => { loadScript: (url: string, callback: () => void) => void } };
  _vidalytics?: { embeds?: Record<string, { options?: EmbedOptions; player?: Player; destroy?: () => void }> };
};

let sharedLoader: Promise<void> | undefined;
const settings = new Map<string, Promise<EmbedOptions>>();

function loadSettings(embedId: string) {
  let pending = settings.get(embedId);
  if (!pending) {
    pending = fetch(`https://fast.vidalytics.com/embeds/${ACCOUNT}/${embedId}/player.settings.json`)
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load Vidalytics settings');
        return response.json() as Promise<EmbedOptions>;
      }).catch((error) => { settings.delete(embedId); throw error; });
    settings.set(embedId, pending);
  }
  return pending;
}

/** One official loader/player runtime, including when the VSL hero loaded it first. */
function loadRuntime(embedId: string) {
  if (sharedLoader) return sharedLoader;
  const w = window as VidalyticsWindow;
  sharedLoader = new Promise<void>((resolve, reject) => {
    const waitForPlayer = () => {
      const deadline = Date.now() + 30000;
      const check = () => {
        if (w.Vidalytics?.Embed && !w.Vidalytics.Embed.isMock) { resolve(); return; }
        if (Date.now() > deadline) { reject(new Error('Vidalytics player unavailable')); return; }
        window.setTimeout(check, 50);
      };
      check();
    };
    const startPlayer = () => {
      const Loader = w.VidalyticsL?.Loader;
      if (!Loader) { reject(new Error('Vidalytics loader unavailable')); return; }
      new Loader().loadScript(`https://fast.vidalytics.com/embeds/${ACCOUNT}/${embedId}/player.min.js`, waitForPlayer);
    };
    if (w.VidalyticsL?.Loader) { startPlayer(); return; }
    const existing = document.querySelector<HTMLScriptElement>('script[src*="fast.vidalytics.com/embeds/"][src$="/loader.min.js"]');
    const script = existing ?? document.createElement('script');
    script.addEventListener('load', startPlayer, { once: true });
    script.addEventListener('error', () => reject(new Error('Unable to load Vidalytics')), { once: true });
    if (!existing) {
      script.async = true;
      script.src = `https://fast.vidalytics.com/embeds/${ACCOUNT}/${embedId}/loader.min.js`;
      document.head.appendChild(script);
    }
  }).catch((error) => { sharedLoader = undefined; throw error; });
  return sharedLoader;
}

export function VidalyticsEmbed({ embedId, title = 'Client interview', className = '' }: {
  embedId: string; title?: string; className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const id = `vidalytics_embed_${embedId}`;
    let cancelled = false;
    let started = false;
    const initialise = async () => {
      if (started || cancelled) return;
      started = true;
      try {
        const options = await loadSettings(embedId);
        if (cancelled) return;
        const w = window as VidalyticsWindow;
        w._vidalytics ??= {};
        w._vidalytics.embeds ??= {};
        const playback = options.playback as Record<string, unknown> | undefined;
        const autoplay = playback?.autoplay as Record<string, unknown> | undefined;
        const clickToPlayOptions = { ...options, playback: { ...playback, autoplay: { ...autoplay, enabled: false, mobile: false } } };
        w._vidalytics.embeds[id] = { options: clickToPlayOptions };
        await loadRuntime(embedId);
        if (cancelled) return;
        w._vidalytics.embeds[id] = { options: clickToPlayOptions };
        const Embed = w.Vidalytics?.Embed;
        if (Embed) {
          await new Embed().run(id);
          if (cancelled) w._vidalytics?.embeds?.[id]?.destroy?.();
        }
      } catch (error) {
        if (!cancelled) console.error('Vidalytics interview failed to initialise', error);
      }
    };
    const observer = typeof IntersectionObserver === 'undefined' ? undefined : new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer?.disconnect();
        void initialise();
      }
    }, { rootMargin: '300px' });
    if (observer) observer.observe(element);
    else void initialise();
    return () => {
      cancelled = true;
      observer?.disconnect();
      const w = window as VidalyticsWindow;
      const embed = w._vidalytics?.embeds?.[id];
      if (embed?.destroy) embed.destroy();
      else {
        embed?.player?.pause?.();
        if (embed?.player?.destroy) embed.player.destroy();
        else embed?.player?.dispose?.();
      }
      if (w.Vidalytics?.embedQueue) w.Vidalytics.embedQueue = w.Vidalytics.embedQueue.filter((entry) => entry.embedId !== id);
      element.replaceChildren();
    };
  }, [embedId]);
  return <div className={className}><div ref={ref} id={`vidalytics_embed_${embedId}`} data-cid={ACCOUNT} data-vid={embedId} aria-label={title} style={{ width: '100%', position: 'relative', paddingTop: '177.78%' }} /></div>;
}